import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import Alert from '../components/ui/Alert';
import Button from '../components/ui/Button';
import Input from '../components/ui/Input';
import LanguageSwitcher from '../components/LanguageSwitcher';
import ThemeToggle from '../components/ThemeToggle';
import logo from '../logo1.svg';

const Login = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { login } = useAuth();

  // La conectare, implicit pe română. Userul poate schimba manual pe EN oricând;
  // după autentificare, limba persistă prin localStorage pe tot parcursul sesiunii.
  useEffect(() => {
    if (i18n.language !== 'ro') {
      i18n.changeLanguage('ro');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [logoutMessage, setLogoutMessage] = useState('');

  useEffect(() => {
    const msg = sessionStorage.getItem('logoutMessage');
    if (msg) {
      setLogoutMessage(msg);
      sessionStorage.removeItem('logoutMessage');
    }
  }, []);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      const result = await login(formData.email, formData.password);
      if (result.success) {
        navigate('/dashboard');
      } else {
        setError(result.error || t('errors.invalidCredentials'));
      }
    } catch (err) {
      setError(err.response?.data?.error || t('errors.invalidCredentials'));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-surface-secondary flex items-center justify-center p-4">
      {/* Selector de limbă + temă în colțul dreapta sus — vizibil înainte de login */}
      <div className="absolute top-4 right-4 flex items-center gap-2">
        <ThemeToggle />
        <LanguageSwitcher />
      </div>

      <div className="w-full max-w-sm">
        {/* Header */}
        <div className="text-center mb-8">
          <img src={logo} alt="SSMPlatform" className="h-48 w-auto mx-auto" />
          <p className="text-sm text-slate-500 dark:text-slate-400 -mt-2">
            {t('auth.loginSubtitle')}
          </p>
        </div>

        {/* Card */}
        <div className="card card-padding">
          {logoutMessage && (
            <Alert type="warning" onDismiss={() => setLogoutMessage('')} className="mb-4">
              {logoutMessage}
            </Alert>
          )}

          {error && (
            <Alert type="error" className="mb-4">
              {error}
            </Alert>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <Input
              label={t('auth.email')}
              type="email"
              name="email"
              required
              value={formData.email}
              onChange={handleChange}
              placeholder="email@firma.ro"
              autoComplete="email"
            />

            <Input
              label={t('auth.password')}
              type="password"
              name="password"
              required
              value={formData.password}
              onChange={handleChange}
              placeholder={t('auth.password')}
              autoComplete="current-password"
            />

            <div className="flex items-center justify-end">
              <Link
                to="/forgot-password"
                className="text-sm text-primary-600 dark:text-primary-300 hover:text-primary-700 font-medium"
              >
                {t('auth.forgotPassword')}
              </Link>
            </div>

            <Button type="submit" loading={isLoading} size="md" className="w-full">
              {isLoading ? t('auth.loggingIn') : t('auth.loginBtn')}
            </Button>
          </form>

          <div className="mt-6 pt-4 border-t border-slate-200 dark:border-slate-700 flex flex-wrap justify-center gap-x-3 gap-y-1 text-xs text-slate-500 dark:text-slate-400">
            <a href="/legal/privacy" className="hover:text-primary-600 dark:hover:text-primary-300">
              {t('legal.privacy')}
            </a>
            <span>·</span>
            <a href="/legal/cookies" className="hover:text-primary-600 dark:hover:text-primary-300">
              {t('legal.cookies')}
            </a>
            <span>·</span>
            <a href="/legal/terms" className="hover:text-primary-600 dark:hover:text-primary-300">
              {t('legal.terms')}
            </a>
            <span>·</span>
            <a href="/legal/about" className="hover:text-primary-600 dark:hover:text-primary-300">
              {t('legal.about')}
            </a>
            <span>·</span>
            <a href="/legal/contact" className="hover:text-primary-600 dark:hover:text-primary-300">
              {t('legal.contact')}
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
